<template>
  <div class="detail">
    <div class="detail__header">
      <DetailHeader :unit="unit" :bulk="false" />
    </div>
    <div class="detail__settings">
      <div class="detail__settings__wrapper">
        <DetailMap :unit="unit" v-if="unit.lat"/>
        <DetailSettings
                :unit="unit"
                :sensors="unit.sensors"
                @dirtyBatch="(value) => dirtyBatch = value"
                :allMoistureSensors="this.getAllMoistureSensors()"
                v-if="refreshUnitComponent"
        />

        <AdvancedDynamicCalibration
          v-if="this.isMoistureSensor() &&
            this.user.is_expert &&
            unit.batch &&
            unit.batch.type && unit.batch.type.properties && !dirtyBatch"
          :batch="unit.batch"
          :sensor="this.getMoistureSensor()"
          :allSensors="this.getAllMoistureSensors()"
          :device="unit"
          :blockedMode="dirtyBatch"
          @changedBatch="refreshUnit"
        />

        <SelfAwareness
                v-if="hasSelfAwareness"
                :device="unit"
        />

        <DynamicSensingTiming
           v-if="unit.version.hasDynamicSensing"
          :device="unit"
        />
      </div>
    </div>
    <div class="detail__charts">
      <div class="detail__charts__wrapper">
        <DetailEventChartPanel :loadedData="eventData" key="{{ $t('detail.events_header') }}" sensor="" :unit="unit" isSensor="false"/>
      </div>
      <div class="detail__charts__wrapper">
        <DetailChartPanel v-for="sensorChart in tentacleTypeCharts" :loadedData="chartData" :key="sensorChart.name" :sensor="sensorChart" :unit="unit" isSensor="true"/>
      </div>
    </div>
  </div>
</template>
<script>
  import {tentacles} from '@/config/tentacles';
  import DetailHeader from '@/views/detail/DetailHeader.vue';
  import DetailSettings from '@/views/detail/DetailSettings.vue';
  import DetailMap from '@/views/detail/DetailMap.vue';
  import DetailChartPanel from '@/views/detail/DetailChartPanel.vue';
  import DetailEventChartPanel from '@/views/detail/DetailEventChartPanel.vue';
  import AdvancedDynamicCalibration from '@/views/detail/AdvancedDynamicCalibration.vue';
  import DynamicSensingTiming from '@/views/detail/DynamicSensingTiming.vue';
  import SelfAwareness from '@/views/detail/SelfAwareness.vue';
  import {mapGetters} from "vuex";

  export default {
  name: 'Detail',
  components: {
    AdvancedDynamicCalibration,
    SelfAwareness,
    DynamicSensingTiming,
    DetailHeader,
    DetailSettings,
    DetailMap,
    DetailChartPanel,
    DetailEventChartPanel,
  },
  data() {
    return {
      dirtyBatch: false,
      chartData: {},
      eventData: [],
      refreshUnitComponent: true,
    };
  },
  computed: {
    ...mapGetters('tentacle', ['user', 'is_expert']),
    hasSelfAwareness() {
      return this.unit.version.saProfiles.length > 1;
    },
    tentacles() {
      return tentacles;
    },
    tentacleTypeCharts() {
      let tentacleType = tentacles.find((element) => element.id === this.unit.typeId);
      return tentacleType.sensorCharts;
    },
    sensors() {
      const tentacleType = this.tentacles.find(tentacle => tentacle.id === this.unit.id);
      return tentacleType ? tentacleType.sensors : [];
    },
    unit() {
      return this.$store.getters['tentacle/unitById'](parseInt(this.$route.params.id, 10));
    },
  },
  created() {
    for (let i = 0; i < this.tentacleTypeCharts.length; i += 1) {
      this.chartData[this.tentacleTypeCharts.name] = {
        id: '',
        name: '',
        unit: '',
        values: null,
      };
    }
  },
  unifiedSensors() {
      let sensorsUnified = {};
      let highestSensorMeasured = {};
      this.unit.sensors.forEach((sensor) => {
          let moistSensorsToUnify = ['Moist_Top', 'Moist_Middle', 'Moist_Bottom'];
          if (moistSensorsToUnify.includes(sensor.sensorId)) {
              if (typeof highestSensorMeasured.measurement !== 'undefined') {
                  if (sensor.measurement > highestSensorMeasured.measurement) {
                      highestSensorMeasured = sensor;
                  }
              } else {
                  highestSensorMeasured = sensor;
              }
          } else {
              sensorsUnified[sensor.sensorId] = sensor;
          }
      });
      sensorsUnified['Moist'] = highestSensorMeasured;
      return sensorsUnified;
  },
  mounted() {
    this.setChartData(this.getDefaultRangeDate(), this.getDateFromToday(0, 0, 0));
    this.setEventData(this.getDefaultRangeDate(), this.getDateFromToday(0, 0, 0));
  },
  watch: { // because of page reload, when unit is not ready, wait for unit and get data
    unit(newUnit, oldUnit) {
      if (newUnit.id) {
        this.setChartData(this.getDefaultRangeDate(), this.getDateFromToday(0, 0, 0));
      }
    },
  },
  methods: {
      isMoistureSensor() {
          return this.unit.sensors.some(sensor =>  this.isMoistureName(sensor.name));
      },
      getMoistureSensor() {
          return this.unit.sensors.find(sensor =>  this.isMoistureName(sensor.name));
      },
      getAllMoistureSensors() {
          return this.unit.sensors.filter(sensor => this.isMoistureName(sensor.name));
      },
      //Method confirms it is name of moisture family (regular, top, middle, bottom)
      isMoistureName(sensorName) {
          let lowerCaseName = sensorName.toLowerCase();
          return lowerCaseName.includes('moisture');
      },
    getDefaultRangeDate() {
      return this.getDateFromToday(0, 0, -1);
    },
    refreshUnit() {
      this.refreshUnitComponent = false;
      this.$nextTick(() => {
        this.refreshUnitComponent = true;
      });
    },
    getDateFromToday(year, month, day) {
      return `${new Date().getFullYear() + year}-${new Date().getMonth() + 1 + month}-${new Date().getDate() + day}`;
    },
    setChartData(fromDate, toDate) {
      let filter = [];
      for (let i = 0; i < this.tentacleTypeCharts.length; i += 1) {
          filter.push({ name: this.tentacleTypeCharts[i].name ,from: fromDate, to: toDate });
      }
      const data = {
        filter: filter,
        tentacle: this.unit.id
      };
      this.$store.dispatch('tentacle/setDetailMeasurement', data).then((resolve) => {
        let result = {};
        if (Object.keys(resolve.data).length > 0) {
            result = resolve.data;
        }
        this.chartData = result;
      });
    },
    setEventData(fromDate, toDate) {
      let filter = [];
      filter.push({ from: fromDate, to: toDate });

      const data = {
        filter: filter,
        tentacle: this.unit.id
      };
      this.$store.dispatch('tentacle/setEvent', data).then((resolve) => {
        let result = [];
        if (resolve.data[0]) {
            result = resolve.data;
        }
        this.eventData = result;
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.detail {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-column-gap: 4rem;
    @media only screen and (max-width: 1400px) {
        width: 100%;
        grid-template-columns: 1fr;
        padding-bottom: 5em;
    }

    &__header {
        grid-column: 1/4;
        @media only screen and (max-width: 1400px) {
            grid-column: 1/1;
        }
    }

    &__settings {
        grid-column: 1/2;
        @media only screen and (max-width: 1400px) {
            margin-top: 2em;
            width: 100%;
            grid-column: 1/1;
        }
    }

    &__charts {
        width: 100%;
        grid-column: 2/4;

        &__wrapper {
            padding-right: 8rem;
        }
        @media only screen and (max-width: 1400px) {
            width: 100%;
            grid-column: 1/1;
            &__wrapper {
                padding-right: 0;
            }
        }
    }
}

</style>
